import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import axios from "axios";
import Swal from "sweetalert2";
import { baseurl, imgurl } from "../baseurl";
import { 
  BiEditAlt, BiSearch, BiFilterAlt, BiChevronLeft, 
  BiChevronRight, BiX, BiPackage, BiCheckShield, 
  BiInfoCircle, BiStats, BiTrendingUp 
} from "react-icons/bi";
import { Link } from "react-router-dom";

function ProductAvailability() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({
    search: "",
    category: "All",
    status: "All",
    priceMin: "",
    priceMax: "",
  });

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseurl}getAllProductsForAdmin`, {
        params: { ...filters, page, limit: 15 }
      });
      setProducts(res.data.products);
      setTotalPages(res.data.totalPages);
      setTotalItems(res.data.totalCount);
    } catch (err) {
      console.error("Fetch error", err);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    const handler = setTimeout(() => { fetchProducts(); }, 400);
    return () => clearTimeout(handler);
  }, [fetchProducts]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const toggleStatus = async (uuid, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    const result = await Swal.fire({
      title: 'Update Status?',
      text: `Are you sure you want to set this product to ${newStatus}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'Yes, update it!'
    });

    if (result.isConfirmed) {
      try {
        await axios.put(`${baseurl}updateProductStatusByAdmin/${uuid}`, { status: newStatus });
        setProducts(prev => prev.map(p => p.uuid === uuid ? { ...p, status: newStatus } : p));
        Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Product status updated.', showConfirmButton: false, timer: 1500 });
      } catch (err) {
        Swal.fire('Error', 'Failed to update status.', 'error');
      }
    }
  };

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper bg-light min-vh-100">
        <div className="container-fluid p-4">
          
          {/* --- 🌟 INFORMATION HERO SECTION --- */}
          <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4">
            <div className="card-body p-2 bg-white">
              <div className="row align-items-center">
                <div className="col-lg-8">
                  <div className="d-flex align-items-center mb-2">
                    <div className="bg-primary bg-opacity-10 p-2 rounded-3 me-3 text-primary">
                      <BiPackage size={32} />
                    </div>
                    <h3 className="fw-bold mb-0">Inventory Control Center</h3>
                  </div>
                  <p className="text-muted mb-0">
                    Manage your global product availability, monitor vendor listings, and ensure pricing accuracy. 
                    Use the real-time filters to audit catalog health and status.
                  </p>
                </div>
                <div className="col-lg-4 d-none d-lg-block">
                    <div className="d-flex justify-content-end gap-3">
                        <div className="text-center">
                            <h4 className="fw-bold mb-0 text-primary">{totalItems}</h4>
                            <small className="text-muted">Total SKUs</small>
                        </div>
                        <div className="vr mx-2"></div>
                        <div className="text-center">
                            <h4 className="fw-bold mb-0 text-success">Active</h4>
                            <small className="text-muted">Live Listings</small>
                        </div>
                    </div>
                </div>
              </div>
            </div>
          </div>

          {/* --- 🛠️ CONTROL BAR --- */}
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <div className="position-relative">
                <span className="position-absolute top-50 start-0 translate-middle-y ps-3 text-muted"><BiSearch /></span>
                <input 
                  type="text" name="search"
                  className="form-control form-control-lg ps-5 border-0 shadow-sm" 
                  placeholder="Search by Product Name, SKU or ID..." 
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
            <div className="col-md-8 d-flex justify-content-md-end gap-2">
              <div className="d-flex bg-white rounded-3 shadow-sm p-1">
                <button className="btn btn-light border-0 px-3" onClick={() => setShowFilter(true)}>
                  <BiFilterAlt className="me-2" /> <span className="d-none d-sm-inline">Refine Results</span>
                </button>
              </div>
            </div>
          </div>

          {/* --- 📦 MAIN DATA TABLE --- */}
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-header bg-white border-bottom py-3">
                <div className="d-flex align-items-center">
                    <BiStats className="text-primary me-2" size={20} />
                    <span className="fw-bold text-uppercase small text-muted">Stock & Availability Report</span>
                </div>
            </div>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0" style={{ fontSize: '13.5px' }}>
                <thead className="bg-light">
                  <tr>
                    <th className="ps-4 py-3 text-muted fw-semibold border-0">PRODUCT DETAILS</th>
                    <th className="border-0 text-muted fw-semibold">SKU</th>
                    <th className="border-0 text-muted fw-semibold">BUSINESS UNIT</th>
                    <th className="border-0 text-muted fw-semibold text-center">PRICING</th>
                    <th className="border-0 text-muted fw-semibold text-center">COMPLIANCE</th>
                    <th className="border-0 text-muted fw-semibold text-end pe-4">MANAGEMENT</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="6" className="text-center py-5"><div className="spinner-border text-primary"></div></td></tr>
                  ) : products.length > 0 ? (
                    products.map((p) => (
                      <tr key={p.uuid}>
                        <td className="ps-4">
                          <div className="d-flex align-items-center">
                            <div className="position-relative me-3">
                                <img src={`${imgurl}${p.featured_image}`} className="rounded-3 shadow-sm" style={{ width: "45px", height: "45px", objectFit: "cover" }} alt="" />
                                <span className={`position-absolute bottom-0 end-0 p-1 border border-white rounded-circle ${p.status === 'active' ? 'bg-success' : 'bg-danger'}`}></span>
                            </div>
                            <div>
                                <div className="fw-bold text-dark">{p.product_name}</div>
                                <div className="text-muted extra-small font-monospace">{p.category_name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="text-muted small">{p.sku}</td>
                        <td>
                            <div className="fw-semibold text-secondary">{p.business_name}</div>
                            <div className="small text-muted italic">Vendor: {p.vendor_name || 'N/A'}</div>
                        </td>
                        <td className="text-center">
                            <span className="fw-bold h6 mb-0 text-dark">₹{p.price}</span>
                        </td>
                        <td className="text-center">
                          <span className={`badge ${p.status === 'active' ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'} px-3 py-2 rounded-pill fw-medium`}>
                             {p.status === 'active' ? <BiCheckShield className="me-1"/> : <BiInfoCircle className="me-1"/>}
                             {p.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="text-end pe-4">
                          <div className="d-flex justify-content-end gap-2">
                            <Link to={`/edit-product/${p.uuid}`} className="btn btn-outline-primary btn-sm border-0 shadow-none"><BiEditAlt size={18} /></Link>
                            <button 
                                className={`btn btn-sm ${p.status === 'active' ? 'btn-outline-warning' : 'btn-outline-success'} border-0`}
                                onClick={() => toggleStatus(p.uuid, p.status)}
                            >
                              {p.status === 'active' ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="6" className="text-center py-5 text-muted">No records found matching the current criteria.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="card-footer bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center">
              <p className="text-muted small mb-0">Showing <strong>{products.length}</strong> listings per page</p>
              <nav className="d-flex align-items-center gap-3">
                <button className="btn btn-sm btn-light border" disabled={page === 1} onClick={() => setPage(p => p - 1)}><BiChevronLeft size={20}/></button>
                <span className="small fw-bold">Page {page} of {totalPages}</span>
                <button className="btn btn-sm btn-light border" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}><BiChevronRight size={20}/></button>
              </nav>
            </div>
          </div>
        </div>

        {/* --- 🔍 FILTER DRAWER --- */}
        <div className={`filter-drawer ${showFilter ? "active" : ""}`}>
          <div className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="fw-bold mb-0">Advanced Filters</h5>
              <button className="btn btn-light rounded-circle" onClick={() => setShowFilter(false)}><BiX size={24}/></button>
            </div>

            <div className="mb-4 text-center p-3 bg-light rounded-3">
                <BiTrendingUp className="text-primary mb-2" size={24} />
                <p className="small text-muted mb-0">Narrow down your search results by category, price range, or listing status.</p>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold small">By Category</label>
              <select className="form-select border-0 bg-light" name="category" value={filters.category} onChange={handleFilterChange}>
                <option value="All">All Categories</option>
                <option value="Electronics">Electronics</option>
                <option value="Fashion">Fashion</option>
                {/* Dynamically Map categories */}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold small">Lifecycle Status</label>
              <select className="form-select border-0 bg-light" name="status" value={filters.status} onChange={handleFilterChange}>
                <option value="All">Show All</option>
                <option value="active">Only Active</option>
                <option value="inactive">Only Inactive</option>
              </select>
            </div>

            <div className="row g-2 mb-4">
              <div className="col-6">
                <label className="form-label fw-semibold small">Price From (₹)</label>
                <input type="number" name="priceMin" className="form-control border-0 bg-light" value={filters.priceMin} onChange={handleFilterChange} />
              </div>
              <div className="col-6">
                <label className="form-label fw-semibold small">Price To (₹)</label>
                <input type="number" name="priceMax" className="form-control border-0 bg-light" value={filters.priceMax} onChange={handleFilterChange} />
              </div>
            </div>

            <button className="btn btn-primary w-100 py-2 shadow-sm fw-bold mb-2" onClick={() => setShowFilter(false)}>Update Results</button>
            <button className="btn btn-link w-100 text-muted text-decoration-none small" onClick={() => setFilters({search: "", category: "All", status: "All", priceMin: "", priceMax: ""})}>Clear all filters</button>
          </div>
        </div>
        {showFilter && <div className="drawer-overlay" onClick={() => setShowFilter(false)}></div>}
      </main>

      <style>{`
        .extra-small { font-size: 11px; }
        .bg-primary-subtle { background-color: #eef2ff !important; }
        .bg-success-subtle { background-color: #ecfdf5 !important; color: #059669 !important; }
        .bg-secondary-subtle { background-color: #f8fafc !important; color: #64748b !important; }
        .filter-drawer { position: fixed; right: -350px; top: 0; width: 350px; height: 100vh; background: #fff; z-index: 2000; transition: 0.4s cubic-bezier(0.4, 0, 0.2, 1); box-shadow: -10px 0 30px rgba(0,0,0,0.05); }
        .filter-drawer.active { right: 0; }
        .drawer-overlay { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(0,0,0,0.2); backdrop-filter: blur(2px); z-index: 1999; }
        .table thead th { font-size: 11px; letter-spacing: 0.03em; }
        .rounded-4 { border-radius: 1rem !important; }
        .form-control-lg { font-size: 1rem; }
      `}</style>
    </>
  );
}

export default ProductAvailability;